import { Ellipsis, Tooltip } from '@zoom/components';
import { TFunction } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomSelectionLabelProps, SelectField } from '../../components/Inputs/SelectField';
import { translateScheduleElementName } from '../../components/MySchedule/transformData';
import { ScheduleElement } from '../../graphql/components';
import { recordBy } from '../../utils/array';
import { formatScheduleElementRGBAColor } from '../../utils/chart';
import { ScheduledActivityLabel } from './ScheduledActivityLabel';

type ScheduledActivitySelectFieldProps = {
  className?: string;
  dataTestId?: string;
  disabled?: boolean;
  label?: string;
  name: string;
  scheduleElements: ScheduleElement[];
  validate?: React.ComponentProps<typeof SelectField>['validate'];
};

export const scheduleElementsToOptions = (scheduleElements: ScheduleElement[], t: TFunction) =>
  scheduleElements.map((element: ScheduleElement) => {
    const elementName = translateScheduleElementName(t, element);

    return {
      id: element.id,
      name: elementName,
    };
  });

export const ScheduledActivitySelectField: React.FC<ScheduledActivitySelectFieldProps> = ({
  className = 'w-100',
  dataTestId = 'scheduled-activity-dropdown',
  disabled,
  label = 'scheduleAdherence.adherenceHistory',
  name,
  scheduleElements,
  validate,
}) => {
  const { t } = useTranslation();

  const [elementsRecordObj, setElementsRecordObj] = useState<Record<string, ScheduleElement>>(() =>
    recordBy(scheduleElements, 'id')
  );

  const [options, setOptions] = useState(() => scheduleElementsToOptions(scheduleElements, t));

  useEffect(() => {
    if (scheduleElements.length) {
      setElementsRecordObj(recordBy(scheduleElements, 'id'));
      setOptions(scheduleElementsToOptions(scheduleElements, t));
    }
  }, [scheduleElements, t]);

  const renderSelectionLabel = ({ value }: CustomSelectionLabelProps) => {
    if (value.length === 0) {
      return null;
    }

    let labelContent = null;
    let labelText = null;

    if (value.length === 1) {
      const selectedElement = elementsRecordObj[value[0]];
      labelText = translateScheduleElementName(t, selectedElement);
      labelContent = (
        <div className="d-flex align-items-center">
          <span
            className="select-scheduled-activity__label-box"
            data-test-id="selected-value-label-box"
            style={{
              backgroundColor: formatScheduleElementRGBAColor(selectedElement.color),
            }}
          />
          {/* @ts-expect-error: Incompatible type of FC from React < v18 */}
          <Ellipsis>{labelText}</Ellipsis>
        </div>
      );
    } else {
      labelText = t('filter.selected', { count: value.length });
      // @ts-expect-error: Incompatible type of FC from React < v18
      labelContent = <Ellipsis>{labelText}</Ellipsis>;
    }

    return (
      <div data-test-id="selected-value-label">
        <Tooltip message={labelText} placement="bottom">
          {labelContent}
        </Tooltip>
      </div>
    );
  };

  return (
    <SelectField
      className={className}
      data-test-id={dataTestId}
      disabled={disabled}
      t={t}
      name={name}
      label={label}
      options={options}
      multiple
      customLabelComponent={(props) => (
        <ScheduledActivityLabel {...props} element={elementsRecordObj[props.value]} className="w-100" />
      )}
      customSelectionLabelComponent={renderSelectionLabel}
      validate={validate}
    />
  );
};
